// ============================================================
// PERRY — RUN SCREEN
// One director, one 3D stage, one DOM overlay. This file exists
// so App.js never has to know how a run is wired — it hands over
// a Soul and gets back "fell" or "arrived".
//
// The director is a HOOK, so it must be called unconditionally at
// the top of a component that only mounts when a run is actually
// happening. That is this component. Mounting it starts the run;
// unmounting it ends the run and cancels the rAF loop.
// ============================================================

import { useEffect, useState } from "react";
import { ACTS } from "./perryData";
import useRunDirector from "./RunDirector";
import CinematicStage from "./CinematicStage";
import QTEOverlay from "./QTEOverlay";
import { CinematicLayer, ensureKeyframes, MetalRule } from "./PerryUI";
import { GM } from "./theme";
import { loadOptions } from "./OptionsMenu";

const KF = `
@keyframes runEndIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes runEndCard { from { opacity: 0; transform: translateY(26px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
`;

export default function RunScreen({ soul, onEvent, onFell, onArrived, actId = "act1" }) {
  ensureKeyframes("perry-run-keyframes", KF);
  const [opts] = useState(loadOptions);
  const [ended, setEnded] = useState(null);   // { how, stats }

  // ══════════════════════════════════════════════════════════
  //  THE END CARD READS THE ACT, 2026-07-30
  // ══════════════════════════════════════════════════════════
  // This file had Hollowmere's name, Hollowmere's soup and the
  // Cinders Road's fell line hardcoded into it, while `ACTS`
  // carried `endTitle`, `endLine`, `fellLine` and `enter` for
  // every act in the title. Act II would have arrived in
  // Candlemarch and been congratulated on reaching Hollowmere.
  // One lookup, and Act III costs nothing here.
  const actRow = ACTS[actId] || ACTS.act1;

  // A SKIPPED road is an arrival — the milestone lands, the fire
  // is lit, the grant pays. It is not the same arrival, and the
  // card must not tell you that you walked something you left.
  const skipped = !!ended?.stats?.skipped;
  const fell = ended?.how === "fell";

  // ── SIGHT — the option goes STRAIGHT THROUGH, unresolved ──
  // This used to collapse to a boolean here, which was fine while
  // there were only two states. There are four now (lettered /
  // coloured / shuffled / dark) and the tier also depends on which
  // encounter is live, which this component has no business
  // knowing. So the raw preference goes to the director, the
  // director owns `sightRef`, and the stage and the overlay both
  // read that one ref — they cannot disagree about what you can
  // see, because there is only one answer to disagree about.
  const director = useRunDirector({
    soul,
    onEvent,
    actId,
    hintMode: opts.hints,
    onFinish: (how, stats) => setEnded({ how, stats }),
  });

  // Respect the shake toggle without touching the director's loop.
  useEffect(() => {
    if (!opts.shake) {
      const id = setInterval(() => { director.shake.current = 0; }, 60);
      return () => clearInterval(id);
    }
    return undefined;
  }, [opts.shake, director.shake]);

  return (
    <div style={{ position: "absolute", inset: 0, background: "#050302", overflow: "hidden" }}>
      <CinematicStage director={director} species={soul?.species} />
      <QTEOverlay director={director} />
      <CinematicLayer
        bars={opts.letterbox}
        grain={opts.grain}
        barPx={54}
        zIndex={50}
      />

      {ended && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 80,
          background: fell
            ? "radial-gradient(ellipse at 50% 50%, rgba(80,10,6,0.72) 0%, rgba(3,2,2,0.96) 70%)"
            : "radial-gradient(ellipse at 50% 60%, rgba(255,176,88,0.16) 0%, rgba(5,3,2,0.95) 70%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          animation: "runEndIn 1.1s ease-out", fontFamily: GM.fontUI,
        }}>
          <div style={{
            width: "min(520px, 92vw)", padding: "30px 30px 26px", borderRadius: 14, textAlign: "center",
            background: "linear-gradient(175deg, #1c1009 0%, #120a08 60%, #0a0605 100%)",
            border: `1px solid ${fell ? GM.roseDim : GM.inkGold}`,
            boxShadow: "0 26px 60px rgba(0,0,0,0.85)",
            animation: "runEndCard 0.8s cubic-bezier(0.2,0.8,0.2,1) 0.4s backwards",
          }}>
            <div style={{
              fontFamily: GM.fontD, fontSize: 34, letterSpacing: 10,
              color: fell ? GM.rose : GM.vellum,
              textShadow: `0 0 30px ${fell ? GM.roseDim : GM.inkGold}`,
            }}>{fell ? "THE BEB FALLS" : skipped ? "THE RIDGE PATH" : (actRow.endTitle || "HOLLOWMERE")}</div>
            <MetalRule width={220} style={{ margin: "12px auto 14px" }} />
            <div style={{
              fontFamily: GM.fontBody, fontSize: 17, lineHeight: 1.65, color: GM.vellumDim,
              marginBottom: 18,
            }}>
              {fell
                ? (actRow.fellLine || "The road keeps what it catches. Everything you learned, you keep — every star, every record, and every splinter of Kindling you answered for. Only the walk is lost.")
                : skipped
                  ? "You went around. The lights were on when you got there, and nobody asked which way you came — but the road was worth walking, and it is still there."
                  : (actRow.endLine || "Lantern light. Voices. Somebody is already bringing soup.")}
            </div>
            <div style={{
              display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap",
              fontSize: 12, letterSpacing: 2, color: GM.vellumFaint, marginBottom: 20,
            }}>
              <span>PARRIES {ended.stats.parries}</span>
              <span>PERFECTS {ended.stats.perfects}</span>
              <span>RUINS {ended.stats.ruins}</span>
              <span style={{ color: GM.amethystBright }}>BEST CHAIN {ended.stats.bestChain}</span>
              {/* Shown on BOTH cards, deliberately. The road paid you
                  even though it beat you — that is the whole reason
                  the per-answer grants exist. */}
              <span style={{ color: GM.inkGoldBright }}>⋔ {ended.stats.kindling || 0}</span>
              {/* Only on a road that actually caught you. On every
                  other road this number is structurally 0 and a
                  counter that always reads 0 teaches nobody
                  anything — same law as the Perfect Streak. */}
              {(ended.stats.spared || 0) > 0 && (
                <span style={{ color: GM.rose }}>SPARED {ended.stats.spared}</span>
              )}
            </div>
            <button
              onClick={() => (fell ? onFell(ended.stats) : onArrived(ended.stats))}
              style={{
                width: "100%", padding: "13px 0", borderRadius: 10, cursor: "pointer",
                background: "linear-gradient(175deg, #a8601e, #4a2810)",
                border: `1px solid ${GM.inkGold}`, color: "#ffe6c2",
                fontFamily: GM.font, fontSize: 14, letterSpacing: 5,
              }}
            >
              {fell ? "STAND BACK UP" : (actRow.enter || "ENTER HOLLOWMERE")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
